import { useCallback, useEffect, useMemo, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type {
  BetaFeedbackCategory,
  BetaFeedbackItem,
  BetaFeedbackSatisfactionAnalytics,
  BetaFeedbackWorkflowStatus,
} from '../types/system';
import {
  formatBetaPageContextSummary,
  getFeedbackPageKey,
  getFeedbackPageLabel,
} from '../utils/betaFeedbackContext';
import { BetaAdminActions } from './BetaAdminUi';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

const CATEGORY_LABELS: Record<BetaFeedbackCategory, string> = {
  bug: 'Bug',
  idea: 'Idée',
  satisfaction: 'Avis',
  other: 'Autre',
};

const CATEGORY_FILTERS: Array<'all' | BetaFeedbackCategory> = [
  'all', 'bug', 'idea', 'satisfaction', 'other',
];

const WORKFLOW_STATUSES: BetaFeedbackWorkflowStatus[] = [
  'new', 'pinned', 'todo', 'in_progress', 'done', 'deferred', 'wontfix',
];

const WORKFLOW_LABELS: Record<BetaFeedbackWorkflowStatus, string> = {
  new: 'Nouveau',
  pinned: 'Épinglé',
  todo: 'À traiter',
  in_progress: 'En cours',
  done: 'Traité',
  deferred: 'Mis de côté',
  wontfix: 'Pas retenu',
};

const OCCURRENCE_LABELS: Record<string, string> = {
  first_time: 'Première fois',
  recurring: 'Récurrent',
  unknown: 'Fréquence inconnue',
};

const RATING_OPTIONS = [0, 1, 2, 3, 4, 5] as const;

function screenshotCount(context: Record<string, unknown>): number {
  if (Array.isArray(context.screenshots)) return context.screenshots.length;
  return context.screenshot ? 1 : 0;
}

function isWorkflowCategory(category: BetaFeedbackCategory): boolean {
  return category === 'bug' || category === 'idea' || category === 'other';
}

interface BetaFeedbackAdminPanelProps {
  session: Session;
}

export default function BetaFeedbackAdminPanel({ session }: BetaFeedbackAdminPanelProps) {
  const [items, setItems] = useState<BetaFeedbackItem[]>([]);
  const [analytics, setAnalytics] = useState<BetaFeedbackSatisfactionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | BetaFeedbackCategory>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | BetaFeedbackWorkflowStatus>('all');
  const [ratingFilter, setRatingFilter] = useState<'all' | (typeof RATING_OPTIONS)[number]>('all');
  const [pageKeyFilter, setPageKeyFilter] = useState<'all' | string>('all');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const showWorkflowFilters = categoryFilter !== 'satisfaction';
  const showRatingFilters = categoryFilter === 'all' || categoryFilter === 'satisfaction';
  const showAnalytics = categoryFilter === 'all' || categoryFilter === 'satisfaction';

  const buildListUrl = useCallback(() => {
    const params = new URLSearchParams({
      pseudo: session.pseudo,
      limit: '150',
    });
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (showWorkflowFilters && statusFilter !== 'all') params.set('status', statusFilter);
    if (showRatingFilters && ratingFilter !== 'all') params.set('rating', String(ratingFilter));
    if (pageKeyFilter !== 'all') params.set('pageKey', pageKeyFilter);
    return `${BACKEND_URL}/api/admin/beta-feedback?${params.toString()}`;
  }, [
    categoryFilter,
    pageKeyFilter,
    ratingFilter,
    session.pseudo,
    showRatingFilters,
    showWorkflowFilters,
    statusFilter,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requests: Promise<Response>[] = [
        adminFetch(buildListUrl(), session.pseudo),
      ];
      if (showAnalytics) {
        requests.push(
          adminFetch(
            `${BACKEND_URL}/api/admin/beta-feedback/satisfaction-analytics?pseudo=${encodeURIComponent(session.pseudo)}`,
            session.pseudo,
          ),
        );
      }

      const [listRes, analyticsRes] = await Promise.all(requests);
      if (!listRes.ok) throw new Error('Chargement des retours impossible');

      const listData = await listRes.json() as { items?: BetaFeedbackItem[] };
      setItems(listData.items || []);

      if (analyticsRes) {
        if (!analyticsRes.ok) throw new Error('Chargement des statistiques avis impossible');
        const analyticsData = await analyticsRes.json() as BetaFeedbackSatisfactionAnalytics;
        setAnalytics(analyticsData);
      } else {
        setAnalytics(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [buildListUrl, session.pseudo, showAnalytics]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStatus = useCallback(async (id: number, status: BetaFeedbackWorkflowStatus) => {
    setUpdatingId(id);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/beta-feedback/${id}?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        },
      );
      const data = await res.json() as { item?: BetaFeedbackItem; error?: string };
      if (!res.ok) throw new Error(data.error || 'Mise à jour impossible');
      if (data.item) {
        setItems(prev => prev.map(item => (item.id === id ? data.item! : item)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setUpdatingId(null);
    }
  }, [session.pseudo]);

  const activeFiltersSummary = useMemo(() => {
    const parts: string[] = [];
    if (categoryFilter !== 'all') parts.push(CATEGORY_LABELS[categoryFilter]);
    if (statusFilter !== 'all' && showWorkflowFilters) parts.push(WORKFLOW_LABELS[statusFilter]);
    if (ratingFilter !== 'all' && showRatingFilters) parts.push(`note ${ratingFilter}`);
    if (pageKeyFilter !== 'all') {
      parts.push(pageKeyFilter === '__global__' ? 'Neo global' : pageKeyFilter);
    }
    return parts;
  }, [categoryFilter, pageKeyFilter, ratingFilter, showRatingFilters, showWorkflowFilters, statusFilter]);

  const resetFilters = () => {
    setCategoryFilter('all');
    setStatusFilter('all');
    setRatingFilter('all');
    setPageKeyFilter('all');
  };

  const renderDistribution = (
    distribution: Record<string, number>,
    total: number,
    onPickRating?: (rating: number) => void,
  ) => (
    <div className="beta-inbox-dist">
      {RATING_OPTIONS.map(rating => {
        const count = distribution[String(rating)] ?? 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <button
            key={rating}
            type="button"
            className={`beta-inbox-dist-cell${count > 0 ? ' beta-inbox-dist-cell--has' : ''}`}
            title={`${count} avis à ${rating}/5`}
            disabled={count === 0}
            onClick={() => onPickRating?.(rating)}
          >
            <span className="beta-inbox-dist-rating">{rating}</span>
            <span className="beta-inbox-dist-bar-wrap">
              <span className="beta-inbox-dist-bar" style={{ width: `${pct}%` }} />
            </span>
            <span className="beta-inbox-dist-count">{count}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="beta-inbox">
      <header className="beta-inbox-header">
        <div>
          <h3 className="beta-inbox-title">Retours testeurs</h3>
          <p className="beta-inbox-subtitle">
            Tri des bugs et idées, analyse des avis par page ou au global.
          </p>
        </div>
        <BetaAdminActions align="end">
          <button type="button" className="beta-admin-btn" onClick={() => void load()} disabled={loading}>
            Actualiser
          </button>
        </BetaAdminActions>
      </header>

      <div className="beta-inbox-filters">
        <div className="beta-inbox-filter-group">
          <span className="beta-inbox-filter-label">Type</span>
          <div className="beta-inbox-filter-pills">
            {CATEGORY_FILTERS.map(key => (
              <button
                key={key}
                type="button"
                className={`beta-inbox-filter-pill${categoryFilter === key ? ' beta-inbox-filter-pill--active' : ''}`}
                onClick={() => {
                  setCategoryFilter(key);
                  if (key === 'satisfaction') setStatusFilter('all');
                  if (key === 'bug' || key === 'idea') setRatingFilter('all');
                }}
              >
                {key === 'all' ? 'Tous' : CATEGORY_LABELS[key]}
              </button>
            ))}
          </div>
        </div>

        {showWorkflowFilters && (
          <div className="beta-inbox-filter-group">
            <span className="beta-inbox-filter-label">Statut</span>
            <div className="beta-inbox-filter-pills beta-inbox-filter-pills--wrap">
              <button
                type="button"
                className={`beta-inbox-filter-pill${statusFilter === 'all' ? ' beta-inbox-filter-pill--active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                Tous
              </button>
              {WORKFLOW_STATUSES.map(status => (
                <button
                  key={status}
                  type="button"
                  className={`beta-inbox-filter-pill${statusFilter === status ? ' beta-inbox-filter-pill--active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {WORKFLOW_LABELS[status]}
                </button>
              ))}
            </div>
          </div>
        )}

        {showRatingFilters && (
          <div className="beta-inbox-filter-group">
            <span className="beta-inbox-filter-label">Note</span>
            <div className="beta-inbox-filter-pills">
              <button
                type="button"
                className={`beta-inbox-filter-pill${ratingFilter === 'all' ? ' beta-inbox-filter-pill--active' : ''}`}
                onClick={() => setRatingFilter('all')}
              >
                Toutes
              </button>
              {RATING_OPTIONS.map(rating => (
                <button
                  key={rating}
                  type="button"
                  className={`beta-inbox-filter-pill${ratingFilter === rating ? ' beta-inbox-filter-pill--active' : ''}`}
                  onClick={() => {
                    setCategoryFilter('satisfaction');
                    setRatingFilter(rating);
                  }}
                >
                  {rating}/5
                </button>
              ))}
            </div>
          </div>
        )}

        {activeFiltersSummary.length > 0 && (
          <div className="beta-inbox-active-filters">
            <span>Filtres : {activeFiltersSummary.join(' · ')}</span>
            <button type="button" className="beta-inbox-clear-filters" onClick={resetFilters}>
              Tout effacer
            </button>
          </div>
        )}
      </div>

      {error && <p className="beta-admin-error">{error}</p>}

      {showAnalytics && analytics && (
        <section className="beta-inbox-analytics">
          <div className="beta-inbox-analytics-global">
            <div className="beta-inbox-stat-card">
              <span className="beta-inbox-stat-label">Avis global Neo</span>
              <strong className="beta-inbox-stat-value">
                {analytics.global.average != null ? `${analytics.global.average}/5` : '—'}
              </strong>
              <span className="beta-inbox-stat-meta">{analytics.global.count} avis</span>
              {renderDistribution(analytics.global.distribution, analytics.global.count, rating => {
                setCategoryFilter('satisfaction');
                setPageKeyFilter('__global__');
                setRatingFilter(rating as (typeof RATING_OPTIONS)[number]);
              })}
            </div>
          </div>

          {analytics.byPage.length > 0 && (
            <div className="beta-inbox-page-table-wrap">
              <table className="beta-inbox-page-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Nb</th>
                    <th>Moyenne</th>
                    <th>Ventilation 0→5</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.byPage.map(page => (
                    <tr
                      key={page.pageKey}
                      className={pageKeyFilter === page.pageKey ? 'beta-inbox-page-row--active' : ''}
                    >
                      <td>
                        <button
                          type="button"
                          className="beta-inbox-page-link"
                          onClick={() => {
                            setCategoryFilter('satisfaction');
                            setPageKeyFilter(page.pageKey);
                          }}
                        >
                          {page.pageLabel}
                        </button>
                      </td>
                      <td>{page.count}</td>
                      <td>{page.average != null ? `${page.average}/5` : '—'}</td>
                      <td>
                        {renderDistribution(page.distribution, page.count, rating => {
                          setCategoryFilter('satisfaction');
                          setPageKeyFilter(page.pageKey);
                          setRatingFilter(rating as (typeof RATING_OPTIONS)[number]);
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="beta-inbox-list-section">
        <div className="beta-inbox-list-head">
          <h4>{loading ? 'Chargement…' : `${items.length} retour${items.length !== 1 ? 's' : ''}`}</h4>
        </div>

        {!loading && items.length === 0 && !error && (
          <p className="beta-admin-empty">Aucun retour pour ces filtres.</p>
        )}

        {items.length > 0 && (
          <ul className="beta-admin-feedback-list beta-inbox-list">
            {items.map(item => {
              const pageLabel = getFeedbackPageLabel(item.context);
              const pageKey = getFeedbackPageKey(item.context);
              const contextSummary = formatBetaPageContextSummary(item.context);
              const rating = typeof item.context?.rating === 'number' ? item.context.rating : null;

              return (
                <li key={item.id} className={`beta-admin-feedback-item beta-inbox-item beta-inbox-item--${item.category}`}>
                  <div className="beta-admin-feedback-item-head">
                    <strong>{item.pseudo_display || item.pseudo}</strong>
                    <span className={`beta-admin-feedback-badge beta-inbox-badge--${item.category}`}>
                      {CATEGORY_LABELS[item.category] || item.category}
                    </span>
                    {rating != null && (
                      <span className="beta-inbox-rating-badge">{rating}/5</span>
                    )}
                    <time dateTime={item.created_at}>
                      {new Date(item.created_at).toLocaleString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  </div>

                  <p className="beta-admin-feedback-message">{item.message}</p>

                  <div className="beta-admin-feedback-meta">
                    <button
                      type="button"
                      className="beta-inbox-page-chip"
                      onClick={() => {
                        setCategoryFilter(item.category === 'satisfaction' ? 'satisfaction' : categoryFilter);
                        setPageKeyFilter(pageKey);
                      }}
                    >
                      {pageLabel}
                    </button>
                    {contextSummary && <span>{contextSummary}</span>}
                    {item.build_id && <span>Build {item.build_id}</span>}
                    {typeof item.context?.occurrence === 'string' && (
                      <span>{OCCURRENCE_LABELS[item.context.occurrence] || item.context.occurrence}</span>
                    )}
                    {screenshotCount(item.context) > 0 && (
                      <span>{screenshotCount(item.context)} capture{screenshotCount(item.context) > 1 ? 's' : ''}</span>
                    )}
                  </div>

                  {isWorkflowCategory(item.category) && (
                    <label className="beta-inbox-status-field">
                      <span>Statut</span>
                      <select
                        value={WORKFLOW_STATUSES.includes(item.status as BetaFeedbackWorkflowStatus)
                          ? item.status
                          : 'new'}
                        disabled={updatingId === item.id}
                        onChange={e => void updateStatus(item.id, e.target.value as BetaFeedbackWorkflowStatus)}
                      >
                        {WORKFLOW_STATUSES.map(status => (
                          <option key={status} value={status}>{WORKFLOW_LABELS[status]}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
